/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable no-unused-expressions */

import React, { useContext, useEffect, useRef, useState } from "react"
import { useHistory, useLocation } from 'react-router-dom';

import { Form, Formik } from 'formik';

import { LoaderContext } from '@fitinsur/contexts';
import { useTextMessage } from '@fitinsur/hooks';
import { MessageCenter } from '@fitinsur/utils';

import { useGetOperationAsync } from '@fitinsur/services/OperationService';
import { useGetQuestionnairesByIdProduct } from '@fitinsur/services/QuestionnairesService';
import { useUpdateAdminAccessRoleOperation, useGetAdminRoles } from '@fitinsur/services/AdminService';
import { useGetAccessRoleById } from '@fitinsur/services/AccessRoleService';

import {
  FormSectionGlobal as FormSection,
  FormContainer,
  HasPermission,
  Button,
  ButtonContainer,
} from '@fitinsur/components'

import { CheckBox } from '@fitinsur/components/Form';

import Layout from 'components/Layout';

import {
  generalHandler,
  accessProfileHandler,
  roleHandler,
} from "./sectionHandlers/index";

import { RootRoleWrapper, HalfWidthWrapper, CheckBoxLabelContainer } from './styles'

export default function ManagementEditAccessRole({ permissions, match, ...props }) {
  const history = useHistory();

  const location = useLocation()

  const formikRef = useRef();
  const getQuestionnaire = useRef(false);
  const sentUpdateAdminOperation = useRef(false);

  const { updateIsLoadingStatus } = useContext(LoaderContext);
  const { getDescription } = useTextMessage();

  const mc = new MessageCenter();

  const [forceLoading, setForceLoading] = useState(false);
  const [operation, setOperation] = useState(null);
  const [accessRoleData, setAccessRoleData] = useState(null);
  const [rolesData, setRolesData] = useState(null);
  const [rolesGroup, setRolesGroup] = useState([]);
  const [isBound, setIsBound] = useState(location.pathname.includes('view'));
  const [questionnaire, setQuestionnaire] = useState([]);

  const SECTIONS = {
    ACCESS_PROFILE: { name: 'ACCESS_PROFILE', isMultiple: false, isRisk: false },
    ROLE: { name: 'ROLE', isMultiple: true, isRisk: false },
  };

  const {
    loading: useGetOperationLoading,
    request: useGetOperationRequest
  } = useGetOperationAsync(match.params.idOperation);

  const {
    data: useGetQuestionnairesByIdProductData,
    loading: useGetQuestionnairesByIdProductLoading,
    status: useGetQuestionnairesByIdProductStatus,
    request: useGetQuestionnairesByIdProductRequest,
  } = useGetQuestionnairesByIdProduct(match.params.idProduct);

  const {
    loading: useGetAccessRoleByIdLoading,
    request: useGetAccessRoleByIdRequest,
  } = useGetAccessRoleById(match.params.idAccessRole);

  const {
    loading: useGetRolesLoading,
    request: useGetRolesRequest,
  } = useGetAdminRoles();

  const {
    loading: useUpdateAdminOperationLoading,
    request: useUpdateAdminOperationRequest,
  } = useUpdateAdminAccessRoleOperation();

  async function getAccessRoleData() {
    if (!match.params.idAccessRole) return;

    await useGetAccessRoleByIdRequest()
      .then((resp) => {
        if (resp.status === 200) {
          setAccessRoleData(resp.data.value);
        }
      });
  }

  async function getRolesData() {
    useGetRolesRequest()
      .then((resp) => {
        if (resp.status === 200) {
          setRolesData(resp.data.value);
        }
        getQuestionnaireData()
      });
  }

  async function getQuestionnaireData() {
    if (getQuestionnaire.current) return;
    getQuestionnaire.current = true

    if (match.params.idOperation) {
      useGetOperationRequest(null, { loadLastDocument: true })
        .then((resp) => {
          if (resp.status === 200) {
            const { data: { value } } = resp;

            setOperation(value);
            setIsBound([].includes(value.idDomainStatus))
          }
        });
    }

    if (match.params.idAccessRole) {
      useGetQuestionnairesByIdProductRequest(null, { idDomain: 752, idDomainStatus: 452 });
    } else {
      useGetQuestionnairesByIdProductRequest(null, { idDomain: 752, idDomainStatus: 451 });
    }
  }

  useEffect(() => {
    getAccessRoleData();
    getRolesData();
  }, []);

  useEffect(() => {
    if (useGetQuestionnairesByIdProductStatus === 200 &&
      ((match.params.idAccessRole && accessRoleData) || !match.params.idAccessRole) &&
      rolesData
    ) {
      generalHandler.events.handleQuestionnaire({
        data: useGetQuestionnairesByIdProductData?.value,
        setQuestionnaire,
        setFieldValue: formikRef?.current?.setFieldValue,
        accessRoleData,
        rolesData,
        setRolesGroup,
        idAccessRole: match.params.idAccessRole,
      });
    }
  }, [
    useGetQuestionnairesByIdProductStatus,
    accessRoleData,
    rolesData,
  ])

  useEffect(() => {
    updateIsLoadingStatus(
      forceLoading ||
      useGetOperationLoading ||
      useGetQuestionnairesByIdProductLoading ||
      useGetRolesLoading ||
      useUpdateAdminOperationLoading
    )
  }, [
    forceLoading,
    useGetOperationLoading,
    useGetQuestionnairesByIdProductLoading,
    useGetRolesLoading,
    useUpdateAdminOperationLoading
  ]);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{}}
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize={true}
    >
      
      {({ values, setFieldValue, errors, setFieldError }) => (
        <Layout
          idOperation={props?.idOperation}
          title={values.fullName || getDescription("DOMAINS", 787)}>
          <Form>
            <FormContainer
              enumerate={1}
              noTitlePadding
              title={getDescription("DOMAINS", 788)}
              hide={!generalHandler.events.sectionIsVisible({
                sectionName: "ACCESS_PROFILE",
                questionnaire,
                permissions,
                currentModality: null,
                ignoreVisibilityByModality: true
              })}
            >
              <FormSection
                setQuestionnaire={setQuestionnaire}
                sectionName="ACCESS_PROFILE"
                disableAll={isBound}
                idProduct={match.params.idProduct}
                values={values}
                errors={errors}
                setFieldValue={setFieldValue}
                setFieldError={setFieldError}
                questionnaire={questionnaire}
                ignoreModality
                permissions={permissions}
                onQuestionChange={(
                  question,
                  value,
                  setFieldValue,
                  questionnaireUpdated
                ) => {
                  accessProfileHandler.events.handleQuestionChange({
                    question,
                    value,
                    questionnaireUpdated,
                    setQuestionnaire,
                    setFieldValue,
                  })
                }}
              />

              <FormContainer
                enumerate={2}
                noTitlePadding
                title={getDescription("DOMAINS", 789)}
                hide={!generalHandler.events.sectionIsVisible({
                  sectionName: "ROLE",
                  questionnaire,
                  permissions,
                  currentModality: null,
                  ignoreVisibilityByModality: true
                })}
              >
                {rolesGroup.map((rootRole, rootRoleIndex) => {
                  const selectedCounter = rootRole.groups.filter(group => group.some(question => question.idQuestion === 1973 && question.idAnswerOption === 7617)).length;

                  return (
                    <RootRoleWrapper key={`rootRole-${rootRoleIndex}`}>
                      <HalfWidthWrapper>
                        <FormContainer
                          collapsedAtStart
                          customHeader={
                            <div style={{ width: '95%', display: "flex", alignItems: "center", justifyContent: "space-between", textTransform: "uppercase", fontWeight: "600", marginTop: '20px' }}>
                              <div style={{ width: '60%', display: 'flex', alignItems: 'center' }}>
                                {`${rootRole.domainTranslatedDescription || rootRole.domainDescription}`}
                              </div>
                              <CheckBoxLabelContainer>
                                <CheckBox
                                  disabled={isBound}
                                  hover={true}
                                  checked={selectedCounter === rootRole.groups.length}
                                  onChange={(value, checked) =>
                                    roleHandler.events.handleSwitchGroupChange({
                                      setFieldValue: formikRef?.current?.setFieldValue,
                                      checked,
                                      rootRoleIndex,
                                      setRolesGroup,
                                    })
                                  }
                                  marginBottom={!rootRoleIndex ? 26 : null}
                                />
                                {getDescription("DOMAINS", 1020)}
                                <span style={{ width: '30px', marginLeft: '10px' }}> ({selectedCounter}/{rootRole.groups.length}) </span>
                              </CheckBoxLabelContainer>
                            </div>
                          }
                          hide={!generalHandler.events.sectionIsVisible({
                            sectionName: "ROLE",
                            questionnaire,
                            permissions,
                            currentModality: null,
                            ignoreVisibilityByModality: true
                          })}
                        >
                          {rootRole?.groups.map((questions, questionsIndex) => (
                            <div key={`role-${questionsIndex}`} style={{ width: "100%" }}>
                              <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0 8px 60px", background: questionsIndex % 2 === 0 ? "#F0F0F0" : "#F7f7f7" }}>
                                <p style={{ width: '80%', color: "#53565A", fontSize: "14px", fontWeight: "700", paddingRight: "20px", marginTop: "10px" }}>
                                  {questions && questions[1]?.label}
                                </p>

                                <div style={{ width: '20%' }}>
                                  <FormSection
                                    setQuestionnaire={(questionnaire) => setRolesGroup(rolesGroup.map((v, i) => {
                                      if (i === rootRoleIndex) {
                                        v.groups[questionsIndex] = questionnaire;
                                      }
                                      return v;
                                    }))}
                                    sectionName="ROLE"
                                    onQuestionChange={(
                                      question,
                                      value,
                                      setFieldValue,
                                      questionnaireUpdated
                                    ) => {
                                      roleHandler.events.handleQuestionChange({
                                        question,
                                        value,
                                        questionnaireUpdated,
                                        setQuestionnaire: (questionnaire) => setRolesGroup(rolesGroup.map((v, i) => {
                                          if (i === rootRoleIndex) {
                                            v.groups[questionsIndex] = questionnaire;
                                          }
                                          return v;
                                        })),
                                        setFieldValue,
                                        setRolesGroup,
                                        rolesGroup,
                                        rootRoleIndex
                                      })
                                    }}
                                    sectionIndex={questionsIndex}
                                    disableAll={isBound}
                                    idProduct={match.params.idProduct}
                                    values={values}
                                    errors={errors}
                                    setFieldValue={setFieldValue}
                                    setFieldError={setFieldError}
                                    questionnaire={questions}
                                    formikRef={formikRef}
                                    permissions={permissions}
                                    verticalAlign="center"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </FormContainer>
                      </HalfWidthWrapper>
                    </RootRoleWrapper>
                  )})}
              </FormContainer>
            </FormContainer>
          </Form>
          <ButtonContainer>
            <Button forth onClick={() => history.push("/management/58/access-role")}>&lt; {getDescription("DOMAINS", 758)}</Button>
            <HasPermission
              permission={!isBound}
            >
              <Button
                id="btnSave"
                type="button"
                onClick={() => generalHandler.events.handleSave({
                  values,
                  questionnaire,
                  idAccessRole: match.params.idAccessRole,
                  idOperation: match.params.idOperation,
                  idOperationParent: operation?.idOperationParent,
                  SECTIONS,
                  rolesGroup,
                  isDraft: false,
                  getDescription,
                  sentUpdateAdminOperation,
                  useUpdateAdminOperationRequest,
                  mc,
                  formikRef,
                  history
                })}
                primary
              >
                {getDescription("DOMAINS", 759)}
              </Button>
            </HasPermission>
          </ButtonContainer>
        </Layout>
      )}
    </Formik >
  )
}